import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { CacheDB } from '@shared/services/indexed-db';
import { LogsData } from '@shared/services/logs';
import { map, Observable, switchMap } from 'rxjs';

/**
 * HTTP interceptor adding authentication token and default headers to outgoing requests.
 *
 * @remarks
 * - Retrieves JWT token from IndexedDB on every request
 * - Applies environment-configured default headers to all requests
 * - Adds `Authorization: Bearer {token}` header when token exists
 *
 * @example
 * ```
 * // In app.config.ts
 * provideHttpClient(withInterceptorsFromDi());
 *
 * // Automatic usage - no manual configuration needed
 * export class UserService {
 *   private http = inject(HttpClient);
 *
 *   getUsers(): Observable<User[]> {
 *     // Headers and auth token automatically added
 *     return this.http.get<User[]>('/api/users');
 *   }
 * }
 * ```
 */
@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  private readonly cacheDB = inject(CacheDB);
  private readonly logs = inject(LogsData);

  /**
   * Intercepts outgoing requests to inject authentication and default headers.
   *
   * @param req - Outgoing HTTP request
   * @param next - Next handler in the interceptor chain
   * @returns Observable of HTTP event with modified request headers
   */
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.cacheDB.get<string>('token').pipe(
      map((token) => token),
      switchMap((jwt) => {
        try {
          const headers = environment.API.options.headers;
          req = req.clone({
            setHeaders: {
              ...headers,
            },
          });
        } catch (e) {
          this.logs.error('RequestInterceptor', 'Error cloning request with headers:', e);
        }
        if (jwt) {
          req = req.clone({
            setHeaders: {
              Authorization: `Bearer ${jwt}`,
            },
          });
        }
        return next.handle(req);
      })
    );
  }
}
